import { PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { DisbursementSchedMap, DisbursementStartPurMap, InputType, PaidByMap } from 'config'
import { type ReactNode, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Modal } from 'stories/components'
import { getPrice1or2decimal, getPrice2decimal, Num } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { OverrideCaclModal } from '../OverrideCalcModal/OverrideCalcModal'
import { PMITypeMap, PrepaidMIWillBeMap } from './mortgageInsurance.constants'

interface MortgageInsuranceModalProps {
  modalData: Record<string, { value: any; key: string }>
  onClose?: Function
  onSubmit?: Function
  canEdit?: boolean
}

const Group = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset className="col-span-12 border border-gray-300 rounded p-3">
    <legend className="px-1 text-sm font-medium text-gray-700">{title}</legend>
    <div className="grid grid-cols-12 gap-3">{children}</div>
  </fieldset>
)

const calcPremium = (data: Record<string, any>) => {
  if (data.premiumOV?.value) return Num(data.premiumOV.value)
  return Num((Num(data.monthsInAdvance?.value) * Num(data.payment?.value)).toFixed(2))
}

const calcReserves = (data: Record<string, any>) => {
  if (data.reserveOV?.value) return Num(data.reserveOV.value)
  return Num((Num(data.monthsInReserve?.value) * Num(data.payment?.value)).toFixed(2))
}

const NUMBER_FIELDS = new Set([
  'miPremiumPct',
  'premiumOV',
  'premiumPBS',
  'monthsInAdvance',
  'monthlyMiPremiumYrs1Pct',
  'monthlyMiPremiumYrs1Amt',
  'monthlyMiPremiumYrs2Pct',
  'monthlyMiPremiumYrs2Amt',
  'ltvCutoffPct',
  'ltvCutoffMonth',
  'monthsInReserve',
  'payment',
  'reservePBS',
])

export const MortgageInsuranceModal = ({
  modalData,
  onClose = () => {},
  onSubmit = () => {},
  canEdit = true,
}: MortgageInsuranceModalProps) => {
  const [data, setData] = useState<Record<string, any>>({})
  const [ovData, setOVData] = useState<any>({})

  useEffect(() => {
    setData(cloneDeep(modalData))
  }, [modalData])

  const onModalClose = () => onClose(false)

  const onModalSubmit = () => onSubmit(true, data)

  const onValueChanged = (key: string, value: any) => {
    if (!canEdit || !data[key]) return
    const temp = cloneDeep(data)
    if (NUMBER_FIELDS.has(key)) temp[key].value = getPrice1or2decimal(value).replaceAll(',', '')
    else temp[key].value = value
    setData(temp)
  }

  const closeOVModal = (save: boolean, ovResult: any) => {
    if (save && ovData.overrideKey) {
      onValueChanged(ovData.overrideKey, ovResult?.ovValue)
    }
    setOVData({})
  }

  const inputProps = (fieldKey: string, input: InputType | any): InputType | any => ({
    ...input,
    value: data[fieldKey]?.value ?? '',
    disabled: input.disabled || !canEdit,
  })

  const premiumTotal = calcPremium(data)
  const reserveTotal = calcReserves(data)
  const netFromWireAmount = data.netFromWire?.value ? premiumTotal : 0

  const paidInHistory = data.premiumGFEDisclosed?.value ?? premiumTotal
  const escrowHistory = data.reservesGFEDisclosed?.value ?? reserveTotal

  return (
    <Modal
      title="Mortgage Insurance"
      titleOkay="OK"
      isOpen
      onClose={onModalClose}
      onOk={onModalSubmit}
      titleCancel="Cancel"
      childLevel={1}
      className="max-w-5xl"
    >
      <>
        <div className="grid grid-cols-12 gap-4 text-sm">
          <div className="col-span-12 lg:col-span-8 flex flex-col gap-4">
            <Group title="MI Type">
              <div className="col-span-8">
                <RenderInput
                  input={inputProps('pmiType', {
                    inputType: 'select',
                    title: 'PMI Type',
                    options: PMITypeMap,
                  })}
                  Key="pmiType"
                  onChange={onValueChanged}
                />
              </div>
              <div className="col-span-4 flex items-end gap-2 pb-1">
                <Button
                  color="white"
                  size="sm"
                  onClick={() =>
                    toast('Copy Estimates will be wired when bytepro keys are provided.', { type: 'info' })
                  }
                >
                  Copy Estimates
                </Button>
                <Button
                  color="white"
                  size="sm"
                  onClick={() => toast('Quote will be wired when bytepro keys are provided.', { type: 'info' })}
                >
                  Quote
                </Button>
              </div>
            </Group>

            <Group title="Mortgage Insurance Premium - Paid in Advance">
              <div className="col-span-12 grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <RenderInput
                    input={inputProps('miPremiumPct', {
                      inputType: 'text',
                      title: 'MI Premium (Line 902)',
                      prefix: '%',
                      contentType: 'number',
                    })}
                    Key="miPremiumPct"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-3">
                  <RenderInput
                    input={inputProps('premiumOV', {
                      inputType: 'text',
                      title: ' ',
                      prefix: '$',
                      contentType: 'number',
                      additionalElements: canEdit ? (
                        <span
                          className="cursor-pointer hover:text-shade-blue"
                          onClick={() =>
                            setOVData({
                              title: 'MI Premium',
                              overrideKey: 'premiumOV',
                              calcValue: Num((Num(data.monthsInAdvance?.value) * Num(data.payment?.value)).toFixed(2)),
                              ovValue: getPrice2decimal(data.premiumOV?.value || ''),
                            })
                          }
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                      ) : undefined,
                    })}
                    Key="premiumOV"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500">Estimates</label>
                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {getPrice2decimal(data.miPremiumEstimatePct?.value || 0)} %
                  </div>
                </div>
                <div className="col-span-2">
                  <RenderInput
                    input={inputProps('premiumPBS', {
                      inputType: 'text',
                      title: 'Paid by Others',
                      prefix: '$',
                      contentType: 'number',
                    })}
                    Key="premiumPBS"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-2">
                  <RenderInput
                    input={inputProps('premiumPaidByOther', {
                      inputType: 'select',
                      title: ' ',
                      options: PaidByMap,
                    })}
                    Key="premiumPaidByOther"
                    onChange={onValueChanged}
                  />
                </div>
              </div>
              <div className="col-span-4">
                <RenderInput
                  input={inputProps('monthsInAdvance', {
                    inputType: 'text',
                    title: 'Months in Advance',
                    contentType: 'number',
                  })}
                  Key="monthsInAdvance"
                  onChange={onValueChanged}
                />
              </div>
              <div className="col-span-4">
                <RenderInput
                  input={inputProps('netFromWire', {
                    inputType: 'checkbox',
                    title: 'Net From Wire',
                  })}
                  Key="netFromWire"
                  onChange={onValueChanged}
                />
              </div>
              <div className="col-span-4">
                <label className="text-xs text-gray-500">Amount</label>
                <div className="border rounded px-2 py-1 bg-gray-50">{getPrice2decimal(netFromWireAmount)}</div>
              </div>
              <div className="col-span-12 text-xs text-gray-500">
                Calculated premium: {getPrice2decimal(premiumTotal)} ({data.monthsInAdvance?.value || 0} mo × $
                {getPrice2decimal(data.payment?.value || 0)}/mo)
              </div>
            </Group>

            <Group title="MI Monthly Payment">
              <div className="col-span-12 grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <RenderInput
                    input={inputProps('monthlyMiPremiumYrs1Pct', {
                      inputType: 'text',
                      title: 'Monthly MI Premium Yrs 1-10',
                      prefix: '%',
                      contentType: 'number',
                    })}
                    Key="monthlyMiPremiumYrs1Pct"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-3">
                  <RenderInput
                    input={inputProps('monthlyMiPremiumYrs1Amt', {
                      inputType: 'text',
                      title: ' ',
                      prefix: '$',
                      contentType: 'number',
                    })}
                    Key="monthlyMiPremiumYrs1Amt"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-3">
                  <label className="text-xs text-gray-500">Estimates</label>
                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {getPrice2decimal(data.monthlyMiPremiumYrs1EstimatePct?.value || 0, false, true)} %
                  </div>
                </div>
              </div>
              <div className="col-span-12 grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <RenderInput
                    input={inputProps('monthlyMiPremiumYrs2Pct', {
                      inputType: 'text',
                      title: 'Monthly MI Premium Yrs 11-30',
                      prefix: '%',
                      contentType: 'number',
                    })}
                    Key="monthlyMiPremiumYrs2Pct"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-3">
                  <RenderInput
                    input={inputProps('monthlyMiPremiumYrs2Amt', {
                      inputType: 'text',
                      title: ' ',
                      prefix: '$',
                      contentType: 'number',
                    })}
                    Key="monthlyMiPremiumYrs2Amt"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-3">
                  <label className="text-xs text-gray-500">Estimates</label>
                  <div className="border rounded px-2 py-1 bg-gray-50">
                    {getPrice2decimal(data.monthlyMiPremiumYrs2EstimatePct?.value || 0, false, true)} %
                  </div>
                </div>
              </div>
              <div className="col-span-12 flex flex-wrap items-end gap-2">
                <span className="text-sm">LTV Cutoff</span>
                <input
                  className="border rounded px-2 py-1 w-16 text-right"
                  value={data.ltvCutoffPct?.value ?? ''}
                  onChange={(e) => onValueChanged('ltvCutoffPct', e.target.value)}
                  readOnly={!canEdit}
                />
                <span className="text-sm">% and / or Month</span>
                <input
                  className="border rounded px-2 py-1 w-16 text-right"
                  value={data.ltvCutoffMonth?.value ?? ''}
                  onChange={(e) => onValueChanged('ltvCutoffMonth', e.target.value)}
                  readOnly={!canEdit}
                />
              </div>
              <div className="col-span-6">
                <RenderInput
                  input={inputProps('prepaidMiWillBe', {
                    inputType: 'select',
                    title: 'Prepaid MI will be',
                    options: PrepaidMIWillBeMap,
                  })}
                  Key="prepaidMiWillBe"
                  onChange={onValueChanged}
                />
              </div>
              <div className="col-span-12 grid grid-cols-12 gap-2 items-end">
                <div className="col-span-2">
                  <RenderInput
                    input={inputProps('monthsInReserve', {
                      inputType: 'text',
                      title: 'Mortgage Insurance Res',
                      contentType: 'number',
                    })}
                    Key="monthsInReserve"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-1 flex items-end pb-2">mths @</div>
                <div className="col-span-2">
                  <RenderInput
                    input={inputProps('payment', {
                      inputType: 'text',
                      title: ' ',
                      prefix: '$',
                      contentType: 'number',
                    })}
                    Key="payment"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-1 flex items-end pb-2">/mo =</div>
                <div className="col-span-2 flex items-end pb-1">
                  <span className="text-shade-blue underline">{getPrice2decimal(reserveTotal)}</span>
                  {canEdit && (
                    <span
                      className="cursor-pointer hover:text-shade-blue ml-1"
                      onClick={() =>
                        setOVData({
                          title: 'Mortgage Insurance Reserves',
                          overrideKey: 'reserveOV',
                          calcValue: Num((Num(data.monthsInReserve?.value) * Num(data.payment?.value)).toFixed(2)),
                          ovValue: getPrice2decimal(data.reserveOV?.value || ''),
                        })
                      }
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                  )}
                </div>
                <div className="col-span-2">
                  <RenderInput
                    input={inputProps('reservePBS', {
                      inputType: 'text',
                      title: 'Paid by Others',
                      prefix: '$',
                      contentType: 'number',
                    })}
                    Key="reservePBS"
                    onChange={onValueChanged}
                  />
                </div>
                <div className="col-span-2">
                  <RenderInput
                    input={inputProps('reservePaidByOther', {
                      inputType: 'select',
                      title: ' ',
                      options: PaidByMap,
                    })}
                    Key="reservePaidByOther"
                    onChange={onValueChanged}
                  />
                </div>
              </div>
              <div className="col-span-6">
                <RenderInput
                  input={inputProps('disbSched', {
                    inputType: 'select',
                    title: 'Disbursement Sched',
                    options: DisbursementSchedMap,
                  })}
                  Key="disbSched"
                  onChange={onValueChanged}
                />
              </div>
              <div className="col-span-6">
                <RenderInput
                  input={inputProps('starting', {
                    inputType: 'select',
                    title: 'Starting',
                    options: DisbursementStartPurMap,
                  })}
                  Key="starting"
                  onChange={onValueChanged}
                />
              </div>
            </Group>

            <Group title="ATR Notes">
              <div className="col-span-12">
                <RenderInput
                  input={inputProps('atrNotes', {
                    inputType: 'textarea',
                    title: 'ATR Notes',
                    rows: 4,
                  })}
                  Key="atrNotes"
                  onChange={onValueChanged}
                />
              </div>
            </Group>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
            <Group title="TRID Good Faith Amount History - Paid in">
              <div className="col-span-12 border rounded p-2 bg-white min-h-[80px]">
                <label className="flex items-center gap-2">
                  <input type="checkbox" disabled className="rounded" />
                  <span>{getPrice2decimal(paidInHistory)}</span>
                </label>
              </div>
            </Group>
            <Group title="TRID Good Faith Amount History - Escrow">
              <div className="col-span-12 border rounded p-2 bg-white min-h-[80px]">
                <label className="flex items-center gap-2">
                  <input type="checkbox" disabled className="rounded" />
                  <span>{getPrice2decimal(escrowHistory)}</span>
                </label>
              </div>
            </Group>
          </div>
        </div>

        {Object.keys(ovData).length > 0 && (
          <OverrideCaclModal
            key={ovData.overrideKey}
            title={ovData.title}
            calcValue={ovData.calcValue}
            ovValue={ovData.ovValue}
            onClose={closeOVModal}
          />
        )}
      </>
    </Modal>
  )
}

export type { MortgageInsuranceFieldKey } from './buildMortgageInsuranceModalData'
export { buildMortgageInsuranceModalData } from './buildMortgageInsuranceModalData'
